import clsx from "clsx";
import type { ReactNode } from "react";
import { Logo } from "./Logo";

/** Page-width container: 1200 px, 24 px gutters. */
export function Container({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx("mx-auto w-full max-w-[1200px] px-6", className)}>{children}</div>;
}

export function SiteHeader({
  nav,
  actions,
  homeHref = "/",
  logoVariant = "positive",
}: {
  nav?: ReactNode;
  actions?: ReactNode;
  homeHref?: string;
  /** Cobalt ("positive") lockup on a light ground, white ("reversed") on a dark one (03_logos.md); "auto" follows the ground (styles-v2.css only). */
  logoVariant?: "positive" | "reversed" | "auto";
}) {
  // data-site-header is the hook styles-v2.css wraps the row on below 24rem (WCAG 2.2 SC 1.4.10
  // Reflow).
  return (
    <header data-site-header="" className="border-b border-rule-subtle bg-canvas">
      <Container className="flex min-h-16 items-center gap-8">
        <a href={homeHref} className="flex shrink-0 items-center no-underline" aria-label="Allergy 123 home">
          <Logo variant={logoVariant} height={28} />
        </a>
        {/* Primary navigation collapses below md; a menu control arrives with the first app that needs more than three links. */}
        {nav && <nav className="hidden items-center gap-6 whitespace-nowrap text-body md:flex">{nav}</nav>}
        {actions && <div className="ml-auto flex shrink-0 items-center gap-3 whitespace-nowrap">{actions}</div>}
      </Container>
    </header>
  );
}

export function SiteFooter({ children }: { children?: ReactNode }) {
  return (
    <footer className="mt-24 border-t border-rule-subtle py-12 text-small text-ink-secondary">
      <Container className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p>© {new Date().getFullYear()} Allergy 123. Allergy 123 is a registered trademark.</p>
        {children}
      </Container>
    </footer>
  );
}

/** Draft content stays labeled draft (brand rule). Render on every non-production build. */
export function DraftBanner({ children = "Draft — content under review; not approved for external use." }: { children?: ReactNode }) {
  return (
    <div role="note" className="border-b border-rule-strong bg-card-tinted px-6 py-2 text-center text-small font-medium text-anchor">
      {children}
    </div>
  );
}

/** Centred panel for authentication and account pages. */
export function AuthPanel({
  title,
  children,
  footer,
  logoVariant = "positive",
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** As SiteHeader: "auto" follows the ground (styles-v2.css only). */
  logoVariant?: "positive" | "reversed" | "auto";
}) {
  return (
    <main className="flex min-h-screen items-start justify-center bg-canvas px-6 py-16">
      <div className="w-full max-w-[480px]">
        <Logo variant={logoVariant} height={32} className="mb-8" />
        <section className="rounded-md border border-rule-strong bg-card p-8">
          <h1 className="mb-4 text-h2 font-semibold text-anchor">{title}</h1>
          {children}
        </section>
        {footer && <div className="mt-6 text-small text-ink-secondary">{footer}</div>}
      </div>
    </main>
  );
}
